import { useState } from 'react';
import { Calendar, Clock, Video, MapPin, ArrowLeft, User, Phone, FileText, Edit3, Save, MessageCircle, Stethoscope } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { 
  useAppointments, 
  useUpdateAppointment, 
  useCancelAppointment,
} from '../../lib/hooks/useAppointments';
import { LoadingState } from '../common/LoadingState';
import { AlertCard } from '../common/AlertCard';
import { EmptyState } from '../common';
import { VideoCallScreen } from './VideoCallScreen';

interface AppointmentsScreenProps {
  onBack?: () => void;
  userRole?: string;
  onNavigate?: (view: string) => void;
}

export function AppointmentsScreen({ onBack, userRole = 'patient', onNavigate }: AppointmentsScreenProps) {
  const [selectedAppointment, setSelectedAppointment] = useState<APIAppointment | null>(null);
  const [appointmentNotes, setAppointmentNotes] = useState<string>('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [activeVideoCall, setActiveVideoCall] = useState<any>(null);

  // Fetch appointments from API
  const { 
    data: appointments, 
    isLoading, 
    error,
    refetch 
  } = useAppointments();
  
  const updateMutation = useUpdateAppointment();
  const cancelMutation = useCancelAppointment();

  // Loading state
  if (isLoading) {
    return <LoadingState message="Loading appointments..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#D4F0F8] p-4">
        <AlertCard
          type="error"
          title="Failed to load appointments"
          description={error.message || 'Unable to fetch appointments'}
          action={<Button onClick={() => refetch()}>Try Again</Button>}
        />
      </div>
    );
  }

  // Filter appointments by status AND date
  // ✅ FIX: Get current date/time for comparison
  const now = new Date();
  
  // ✅ FIX: Sort all appointments by scheduled_date in ascending order first
  const sortedAppointments = [...(appointments || [])].sort((a, b) => {
    return new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime();
  });

  // ✅ FIX: Upcoming appointments must be in the future AND have active status
  const upcomingAppointments = sortedAppointments.filter(apt => {
  const aptDateTime = new Date(apt.scheduled_date);
  const diffMs = aptDateTime - now;                  // appointment - now
  const diffMinutes = diffMs / (1000 * 60);          // convert to minutes

  const isActiveStatus =
    apt.status === "Scheduled" || apt.status === "Confirmed";

  // Include if it's in the future OR within the last 30 mins
  const isUpcomingOrRecent =
    diffMinutes >= 0 || (diffMinutes < 0 && diffMinutes >= -30);

  return isActiveStatus && isUpcomingOrRecent;
});

  
  // ✅ FIX: Past appointments are in the past OR have completed/cancelled status
  const pastAppointments = sortedAppointments.filter(
    apt => {
      const aptDateTime = new Date(apt.scheduled_date);
      // Compare with current time, not just current date
      const isPast = aptDateTime < now;
      const isCompletedStatus = apt.status === 'Completed' || apt.status === 'Cancelled' || apt.status === 'No Show';
      
      if (userRole === 'doctor') {
        // Doctors see missed/no-show appointments AND past scheduled appointments
        return apt.status === 'No Show' || apt.status === 'Cancelled' || 
               (isPast && (apt.status === 'Scheduled' || apt.status === 'Confirmed'));
      } else {
        // Other roles see all completed/past appointments (by status OR by date)
        return isCompletedStatus || isPast;
      }
    }
  );

  const handleSaveNotes = () => {
    if (selectedAppointment) {
      updateMutation.mutate({
        id: selectedAppointment.id,
        data: {
          consultation_notes: appointmentNotes
        }
      }, {
        onSuccess: () => {
          setIsEditingNotes(false);
          toast.success('Notes saved successfully!');
        }
      });
    }
  };

  const handleCancelAppointment = (appointmentId: number) => {
    cancelMutation.mutate(appointmentId, {
      onSuccess: () => {
        setSelectedAppointment(null);
        refetch();
      }
    });
  };

  const getTypeColor = (type: string) => {
    switch (type.toUpperCase()) {
      case 'EMERGENCY':
        return 'bg-destructive/10 text-destructive border-destructive';
      case 'FOLLOW_UP':
      case 'FOLLOW-UP':
        return 'bg-primary/10 text-primary border-primary';
      case 'IN_PERSON':
      case 'TELECONSULTATION':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      default:
        return 'bg-secondary';
    }
  };

  const formatAppointmentType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to check if appointment can be joined
  const canJoinAppointment = (datetime: string) => {
    const now = new Date();
    const [date, time] = datetime.split("T");
    // Parse the appointment date and time
    const [year, month, day] = date.split('-').map(Number);
    const timeStr = time.toLowerCase();
    const [timeOnly, period] = timeStr.split(' ');
    const [hours, minutes] = timeOnly.split(':').map(Number);
    
    // Convert to 24-hour format
    let appointmentHours = hours;
    if (period === 'pm' && hours !== 12) {
      appointmentHours = hours + 12;
    } else if (period === 'am' && hours === 12) {
      appointmentHours = 0;
    }
    
    const appointmentDate = new Date(year, month - 1, day, appointmentHours, minutes);
    
    // Allow joining if appointment time is now or in the past
    return appointmentDate <= now;
  };

  // Handle joining video call
  const handleJoinCall = (appointment: any) => {
    setActiveVideoCall(appointment);
  };

  // Handle ending video call
  const handleEndCall = () => {
    setActiveVideoCall(null);
    toast.success('Call ended successfully');
  };

  const formatter = new Intl.DateTimeFormat("en-US", {
    dateStyle: "long",
    timeStyle: "short"
  });

  // Show video call screen if active
  if (activeVideoCall) {    
    return (
      <VideoCallScreen
        sessionId='TestSession#1'
        patientName={activeVideoCall.patient_name}
        doctorName={activeVideoCall.doctor_name}
        onEndCall={handleEndCall}
      />
    );
  }

  // Main Appointments List View
  return (
    <div className="min-h-screen bg-[#D4F0F8] pb-20">
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="text-primary-foreground">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div>
            <h1 className="text-primary-foreground">Appointments</h1>
            <p className="text-primary-foreground/80">Manage your healthcare visits</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <Card>
          <CardContent className="p-6">
            {upcomingAppointments.length === 0 ? (
              <EmptyState 
                icon={Calendar}
                title="No appointments scheduled"
                description="Schedule your first appointment to get started"
              />
            ) : (
              <div className="space-y-4">
                {upcomingAppointments.map((apt: any) => (
                  <Card key={apt.id} className="border-accent/20 hover:shadow-lg transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-accent" />
                            <h3 className="text-primary">{apt.patientName || apt.patient_name}</h3>
                          </div>
                          <div className="space-y-1 text-sm text-muted-foreground ml-6">
                            {apt.reason && (
                              <p className="flex items-center gap-2 ">
                                <MessageCircle className="w-3 h-3" />
                                <b>{apt.reason}</b> 
                              </p>
                            )}
                            <p className="flex items-center gap-2">
                              <Stethoscope className="w-3 h-3" />
                              Participants : {apt.doctorName || apt.doctor_name} (Dr.),  &nbsp;
                              {apt.assistant_name} (Physician Asst.), &nbsp;
                              {apt.therapist_name} (Therapist), &nbsp;
                              {apt.nurse_name} (Home Nurse)
                            </p>
                            <p className="flex items-center gap-2">
                              <Clock className="w-3 h-3" />
                              {formatter.format(new Date(apt.scheduled_date))}
                            </p>                                
                          </div>
                        </div>
                        <Badge className={`${
                          apt.status === 'Confirmed' || apt.status === 'CONFIRMED'
                            ? 'bg-[#14CAF4] text-white'
                            : apt.status === 'Pending' || apt.status === 'PENDING'
                            ? 'bg-[#F07D13] text-white'
                            : apt.status === 'Completed' || apt.status === 'COMPLETED'
                            ? 'bg-[#2762AC] text-white'
                            : 'bg-[#494949] text-white'
                        }`}>
                          {apt.status}
                        </Badge>
                        {canJoinAppointment(apt.scheduled_date) && (
                          <Button
                            size="sm"
                            onClick={() => handleJoinCall(apt)}
                            className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white text-xs ml-2"
                          >
                            <Video className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            Join Call
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card className="shadow-lg bg-accent/10 border-accent">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center">
                <Phone className="w-6 h-6 text-accent-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="text-primary">Need urgent care?</h3>
                <p className="text-muted-foreground">Contact emergency helpline</p>
              </div>
              <Button className="bg-accent hover:bg-accent/90">
                Call Now
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}